/**
 * useOrganizationSubscriptions Hook
 * Super Admin용 전체 조직 구독 관리 커스텀 훅
 */

import { useEffect, useState, useCallback } from 'react';
import {
  SubscriptionAdminRepository,
  OrganizationSubscription,
  UpdatePlanRequest,
  SubscriptionDetail,
} from '../repositories/SubscriptionAdminRepository';

const repository = new SubscriptionAdminRepository();

export interface SubscriptionStats {
  total: number;
  trial: number;
  active: number;
  expired: number;
  expiringSoon: number; // 7일 이내 만료
}

export const useOrganizationSubscriptions = () => {
  const [items, setItems] = useState<OrganizationSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * 전체 조직 목록 로드
   */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await repository.getAllOrganizations();
      setItems(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 플랜 업데이트
   */
  const updatePlan = useCallback(
    async (
      organizationId: string,
      request: UpdatePlanRequest
    ): Promise<boolean> => {
      try {
        await repository.updatePlan(organizationId, request);
        // 목록 새로고침
        await fetchAll();
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : '플랜 업데이트에 실패했습니다.';
        setError(errorMessage);
        return false;
      }
    },
    [fetchAll]
  );

  /**
   * 구독 상세 정보 조회
   */
  const getDetail = useCallback(
    async (organizationId: string): Promise<SubscriptionDetail | null> => {
      try {
        const detail = await repository.getSubscription(organizationId);
        return detail;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '조회에 실패했습니다.';
        setError(errorMessage);
        return null;
      }
    },
    []
  );

  /**
   * 통계 계산 (클라이언트 측)
   */
  const calculateStats = useCallback((): SubscriptionStats => {
    const now = new Date();
    const sevenDaysLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const stats: SubscriptionStats = {
      total: items.length,
      trial: 0,
      active: 0,
      expired: 0,
      expiringSoon: 0,
    };

    items.forEach((org) => {
      // 상태별 카운트
      if (org.subscription_status === 'trial') {
        stats.trial++;
      } else if (org.subscription_status === 'active') {
        stats.active++;
      } else if (org.subscription_status === 'expired') {
        stats.expired++;
      }

      // 7일 이내 만료 예정
      const endsAt =
        org.subscription_status === 'trial'
          ? org.trial_ends_at
          : org.plan_ends_at;

      if (endsAt) {
        const endsDate = new Date(endsAt);
        if (endsDate <= sevenDaysLater && endsDate > now) {
          stats.expiringSoon++;
        }
      }
    });

    return stats;
  }, [items]);

  /**
   * 남은 일수 계산
   */
  const getDaysRemaining = useCallback(
    (org: OrganizationSubscription): number | null => {
      const endsAt =
        org.subscription_status === 'trial'
          ? org.trial_ends_at
          : org.plan_ends_at;

      if (!endsAt) return null;

      const now = new Date();
      const endsDate = new Date(endsAt);
      const diff = endsDate.getTime() - now.getTime();
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

      return days;
    },
    []
  );

  /**
   * 필터링
   */
  const filterByPlan = useCallback(
    (plan: string | null): OrganizationSubscription[] => {
      if (!plan) return items;
      return items.filter((org) => org.subscription_plan === plan);
    },
    [items]
  );

  const filterByStatus = useCallback(
    (status: string | null): OrganizationSubscription[] => {
      if (!status) return items;
      return items.filter((org) => org.subscription_status === status);
    },
    [items]
  );

  const search = useCallback(
    (query: string): OrganizationSubscription[] => {
      if (!query) return items;

      const lowerQuery = query.toLowerCase();
      return items.filter(
        (org) =>
          org.name?.toLowerCase().includes(lowerQuery) ||
          org.owner_name?.toLowerCase().includes(lowerQuery) ||
          org.email?.toLowerCase().includes(lowerQuery) ||
          org.phone?.includes(query)
      );
    },
    [items]
  );

  // 마운트 시 데이터 로드
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return {
    items,
    loading,
    error,
    fetchAll,
    updatePlan,
    getDetail,
    calculateStats,
    getDaysRemaining,
    filterByPlan,
    filterByStatus,
    search,
  };
};
